import type { UseCaseContent } from "@/lib/use-cases/content";

export const ES_USE_CASES: Partial<Record<string, UseCaseContent>> = {
  clippers: {
    related: [
      "/features/auto-clip",
      "/features/video-cutter",
      "/calculators/clips-from-one-video",
      "/calculators/views-to-money",
    ],
    navTitle: "Clippers",
    seoTitle: "Gana dinero clipeando videos: campañas de clipping",
    seoDescription:
      "Encuentra campañas de clipping, corta clips virales con IA y haz seguimiento de tus vistas, todo dentro de Crayo. La forma más rápida de empezar a clipear y cobrar por ello.",
    heroPrefix: "Gana dinero",
    heroHighlight: "clipeando videos",
    heroSuffix: "de los creadores que ya sigues",
    description:
      "Explora campañas de clipping activas en la Content Library de Crayo, corta clips en minutos con IA y sigue cada vista. Encuentra el trabajo y hazlo en una sola app.",
    buttonText: "Ver campañas",
    buttonLink: "https://crayo.ai/content-library",
    steps: [
      {
        title: "Encuentra una campaña",
        description:
          "Abre la Content Library y explora campañas activas con recompensas por clip y material fuente listo para usar.",
      },
      {
        title: "Clipea en minutos",
        description:
          "Auto Clip encuentra los momentos más fuertes, o usa Clip It para cortar exactamente lo que quieres, con subtítulos aplicados automáticamente.",
      },
      {
        title: "Publica y haz seguimiento",
        description:
          "Publica en tus cuentas y sigue tus vistas con View Tracker, para saber siempre qué está funcionando.",
      },
    ],
    benefits: [
      {
        title: "Las campañas llegan a ti",
        description:
          "Sin mensajes en frío ni búsquedas eternas en Discord. Las campañas activas viven dentro de Crayo con el material adjunto.",
      },
      {
        title: "Momentos virales, encontrados por ti",
        description:
          "Auto Clip analiza videos largos y te muestra los momentos que vale la pena publicar, para que no pases horas revisando VODs completos.",
      },
      {
        title: "Subtítulos en segundos",
        description:
          "Cada clip recibe subtítulos limpios y con estilo de forma automática, la diferencia entre un clip que se mira y uno que se pasa de largo.",
      },
      {
        title: "Conoce tus números",
        description:
          "View Tracker sigue tus publicaciones en todas tus cuentas, así que medir el rendimiento deja de ser un trabajo de hoja de cálculo.",
      },
      {
        title: "No necesitas saber editar",
        description: "Si sabes elegir un buen momento, Crayo se encarga del resto.",
      },
      {
        title: "Todo en una pestaña",
        description:
          "Campañas, clipping, subtítulos y seguimiento viven en una sola app, sin saltar entre herramientas para encontrar trabajo y entregarlo.",
      },
    ],
    relatedFeatures: ["auto-clip", "video-cutter", "video-crop", "youtube-downloader"],
    faqs: [
      {
        question: "¿Cómo encuentro campañas de clipping?",
        answer:
          "Están integradas en la Content Library de Crayo. Ábrela, revisa lo que está activo y empieza a clipear. Los videos fuente los aporta la campaña.",
      },
      {
        question: "¿Cómo funcionan los pagos?",
        answer:
          "Cada campaña define sus propios términos y recompensas, visibles antes de empezar a clipear. Lee los detalles de la campaña, clipea, publica y recibe la recompensa según sus términos.",
      },
      {
        question: "¿Necesito experiencia en edición?",
        answer:
          "No. Auto Clip encuentra los momentos, los subtítulos son automáticos y el editor se encarga de recortes y encuadres. Importa más el criterio que la técnica.",
      },
      {
        question: "¿Puedo clipear a creadores que no tienen campañas activas?",
        answer:
          "Sí. Pega cualquier enlace de YouTube o sube el material y clipéalo de la misma forma. Las campañas son solo la ruta más rápida para cobrar.",
      },
    ],
  },
  "podcast-clipping": {
    related: [
      "/compare/crayo-vs-descript",
      "/compare/crayo-vs-riverside-magic-clips",
      "/features/auto-clip",
      "/features/speech-enhancer",
      "/use-cases/agencies",
      "/calculators/clips-from-one-video",
    ],
    navTitle: "Clips de podcast",
    seoTitle: "Generador de clips de podcast: de episodios a shorts",
    seoDescription:
      "Convierte cada episodio de podcast en una semana de clips cortos. La IA encuentra los mejores momentos, limpia el audio, agrega subtítulos y recorta para TikTok, Reels y Shorts.",
    heroPrefix: "Convierte un episodio en",
    heroHighlight: "una semana de clips",
    heroSuffix: "",
    description:
      "Sube un episodio, o solo un enlace de YouTube, y deja que la IA encuentre los momentos que vale la pena publicar. Subtítulos, recorte vertical y audio limpio incluidos.",
    buttonText: "Clipea un episodio gratis",
    buttonLink: "https://crayo.ai/create/auto-clip",
    steps: [
      {
        title: "Agrega tu episodio",
        description:
          "Sube un archivo o pega un enlace de YouTube con el descargador integrado. Los episodios completos son bienvenidos.",
      },
      {
        title: "Deja que la IA encuentre los momentos",
        description:
          "Auto Clip analiza toda la conversación y extrae los segmentos con más probabilidad de enganchar a quien está scrolleando.",
      },
      {
        title: "Pule y publica",
        description:
          "El mejorador de voz limpia el audio, los subtítulos se aplican automáticamente y cada clip se recorta a 9:16 para TikTok, Reels y Shorts.",
      },
    ],
    benefits: [
      {
        title: "Deja de revisar líneas de tiempo",
        description:
          "Una hora de conversación, analizada en minutos. Revisas clips, no material en bruto.",
      },
      {
        title: "Audio de podcast, arreglado",
        description:
          "El mejorador de voz limpia el eco de la sala y los niveles irregulares para que los clips suenen como la versión de estudio.",
      },
      {
        title: "Subtítulos que sostienen el clip",
        description:
          "La mayoría de la gente mira video corto sin sonido. Cada clip sale con subtítulos precisos y con estilo.",
      },
      {
        title: "Vertical sin el recorte incómodo",
        description:
          "Reencuadra episodios horizontales a 9:16 de forma limpia en vez de publicar video con franjas negras que nadie mira.",
      },
      {
        title: "Funciona desde un enlace",
        description:
          "Sin exportar desde tu plataforma de podcast. Pega la URL de YouTube y empieza a clipear.",
      },
      {
        title: "No solo podcasts",
        description:
          "Webinars, sesiones de coaching, lecciones de cursos, entrevistas: si es contenido largo hablado, Crayo lo clipea.",
      },
    ],
    relatedFeatures: ["auto-clip", "speech-enhancer", "youtube-downloader", "video-crop"],
    faqs: [
      {
        question: "¿Cómo sabe Crayo qué momentos clipear?",
        answer:
          "Auto Clip analiza el episodio completo y muestra los segmentos con los ganchos más fuertes, como historias, opiniones polémicas y remates, y luego tú eliges cuáles publicar.",
      },
      {
        question: "Soy coach o creo cursos, no tengo podcast. ¿Funciona con mi contenido?",
        answer:
          "Sí. Cualquier contenido largo y hablado, desde webinars y talleres hasta módulos de cursos y llamadas con clientes, se clipea exactamente igual.",
      },
      {
        question: "¿Puedo clipear directamente desde YouTube?",
        answer:
          "Sí. Pega el enlace, Crayo descarga el video con el descargador integrado y en minutos estás clipeando. Clipea solo contenido sobre el que tengas derechos.",
      },
      {
        question: "¿Cómo se ven los clips terminados?",
        answer:
          "Video vertical 9:16 con subtítulos integrados y audio limpio, listo para publicar en TikTok, Reels y Shorts tal cual.",
      },
    ],
  },
  streamers: {
    related: [
      "/features/auto-clip",
      "/features/video-crop",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/compare/crayo-vs-eklipse",
      "/compare/crayo-vs-streamladder",
    ],
    navTitle: "Streamers",
    seoTitle: "Creador de clips de Twitch y Kick para streamers",
    seoDescription:
      "Convierte los VODs de tus streams en shorts que hacen crecer tu canal. La IA encuentra tus momentos de hype, agrega subtítulos y formatea los clips para TikTok, Reels y Shorts.",
    heroPrefix: "Convierte tus",
    heroHighlight: "VODs de stream",
    heroSuffix: "en shorts que hacen crecer el canal",
    description:
      "Crayo convierte VODs de Twitch y Kick en clips verticales cortos encontrando los momentos de mayor energía y formateándolos para TikTok, Reels y Shorts. Es para streamers que quieren clips hechos con el material que ya grabaron.",
    buttonText: "Clipea tu VOD gratis",
    buttonLink: "https://crayo.ai/create/auto-clip",
    steps: [
      {
        title: "Trae un VOD",
        description:
          "Sube tu VOD de Twitch o Kick, o toma la exportación de tu stream desde YouTube con el descargador integrado.",
      },
      {
        title: "La IA encuentra el hype",
        description:
          "Auto Clip analiza horas de stream y extrae las jugadas clave, los momentos de rabia y los segmentos que hicieron explotar el chat.",
      },
      {
        title: "Formatea en vertical y publica",
        description:
          "Diseño para streamers con tu facecam al frente, subtítulos automáticos, recorte 9:16, exportar.",
      },
    ],
    benefits: [
      {
        title: "Tus VODs son una mina de contenido",
        description:
          "Cada stream ya contiene tus mejores clips cortos. Crayo los saca para que dejen de morir en tu archivo.",
      },
      {
        title: "Clipea después de dormir, no en vez de dormir",
        description:
          "Streamear seis horas y luego editar tres es como los streamers se queman. Auto Clip hace la revisión por ti.",
      },
      {
        title: "Diseños con la facecam al frente",
        description:
          "Formato pensado para streamers: tu cámara y el gameplay, organizados para una pantalla vertical.",
      },
      {
        title: "Ordena el caos del audio",
        description:
          "El mejorador de voz controla el audio del juego que se cuela y las inconsistencias del micrófono para que tus comentarios se escuchen bien.",
      },
      {
        title: "Los shorts son tu motor de descubrimiento",
        description:
          "TikTok y Shorts llegan a gente que nunca va a recorrer un directorio de Twitch. Publicar clips con constancia es como los streamers pequeños se hacen notar.",
      },
      {
        title: "Publica mientras el momento está caliente",
        description:
          "Clipea el stream de esta noche en minutos, para que el short salga mientras el chat todavía habla de eso.",
      },
    ],
    relatedFeatures: ["auto-clip", "speech-enhancer", "video-crop", "video-cutter"],
    faqs: [
      {
        question: "¿Funciona tanto con Twitch como con Kick?",
        answer:
          "Sí. Trae tu VOD de cualquiera de las dos plataformas (o tu archivo de streams en YouTube) y clipéalo de la misma forma.",
      },
      {
        question: "Mis streams duran más de 4 horas. ¿Puede con eso?",
        answer:
          "Justo para eso está. Auto Clip está hecho para material largo. Analiza el VOD completo y te entrega los momentos, así que la duración es una ventaja, no un problema.",
      },
      {
        question: "¿Los clips realmente hacen crecer un stream?",
        answer:
          "El video corto es donde los nuevos espectadores descubren a streamers que todavía no son grandes. Los clips no reemplazan un buen stream, pero son la fuente de descubrimiento más confiable que tiene un streamer pequeño.",
      },
      {
        question: "¿Necesito mi facecam en cada clip?",
        answer:
          "No. El diseño para streamers es opcional, así que puedes usar gameplay a pantalla completa, facecam al frente o un diseño dividido en cada clip.",
      },
    ],
  },
};
